/**
 * MetadataForm — Reusable form for organization/location metadata fields.
 * Renders every field dynamically from field definitions (utils/organizationMetadataFields.ts).
 * Supports inheritance: location fields show org-level values as placeholders.
 * Companion fields (a "Last Verified" / "Completed On" date, a note) render on the same row as the field they
 * belong to. Read-only dates display as mm/dd/yyyy.
 */

import React, { useState } from "react";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import {
  METADATA_CATEGORIES,
  getCompanionFields,
  getPrimaryFieldsByCategory,
} from "../../utils/organizationMetadataFields";
import { formatDateMDY } from "../../utils/formatters";
import type {
  MetadataFieldDef,
  MetadataCategoryId,
} from "../../utils/organizationMetadataFields";

interface MetadataFormProps {
  metadata: Record<string, unknown>;
  inheritedMetadata?: Record<string, unknown>;
  onChange: (metadata: Record<string, unknown>) => void;
  readOnly?: boolean;
  /** Render only these categories (default: all). */
  categories?: MetadataCategoryId[];
}

const MetadataForm: React.FC<MetadataFormProps> = ({
  metadata,
  inheritedMetadata,
  onChange,
  readOnly = false,
  categories,
}) => {
  const visibleCategories = categories
    ? METADATA_CATEGORIES.filter((c) => categories.includes(c.id))
    : METADATA_CATEGORIES;
  const [expandedCategories, setExpandedCategories] = useState<
    Set<MetadataCategoryId>
  >(new Set(METADATA_CATEGORIES.map((c) => c.id)));

  const toggleCategory = (id: MetadataCategoryId) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const getValue = (key: string): unknown => {
    if (key in metadata && metadata[key] !== undefined) {
      return metadata[key];
    }
    return undefined;
  };

  const getEffectiveValue = (key: string): unknown => {
    const direct = getValue(key);
    if (direct !== undefined) {
      return direct;
    }
    if (inheritedMetadata && key in inheritedMetadata) {
      return inheritedMetadata[key];
    }
    return undefined;
  };

  const isOverridden = (key: string): boolean => {
    return (
      inheritedMetadata !== undefined &&
      key in metadata &&
      metadata[key] !== undefined
    );
  };

  const isInherited = (key: string): boolean => {
    return (
      inheritedMetadata !== undefined &&
      !(key in metadata && metadata[key] !== undefined) &&
      inheritedMetadata[key] !== undefined
    );
  };

  const handleChange = (key: string, value: unknown) => {
    onChange({ ...metadata, [key]: value });
  };

  const handleReset = (key: string) => {
    const next = { ...metadata };
    delete next[key];
    onChange(next);
  };

  const renderField = (field: MetadataFieldDef) => {
    const value = getValue(field.key);
    const effectiveValue = getEffectiveValue(field.key);
    const inherited = isInherited(field.key);
    const overridden = isOverridden(field.key);

    return (
      <div
        key={field.key}
        className="relative"
        data-testid={`metadata-field-${field.key}`}
      >
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-gray-700">
            {field.label}
            {inherited && (
              <span className="ml-2 text-xs text-blue-500 font-normal">
                (inherited from org)
              </span>
            )}
          </label>
          {overridden && !readOnly && (
            <button
              type="button"
              onClick={() => handleReset(field.key)}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              Reset to org default
            </button>
          )}
        </div>
        {field.hint && (
          <p className="mb-1 text-xs text-gray-500">{field.hint}</p>
        )}
        {renderFieldInput(field, value, effectiveValue, inherited)}
      </div>
    );
  };

  /** A primary field plus its companions on one row. */
  const renderRow = (field: MetadataFieldDef) => {
    const companions = getCompanionFields(field.key);
    if (companions.length === 0) {
      return renderField(field);
    }
    const cols = companions.length >= 2 ? "md:grid-cols-3" : "md:grid-cols-2";
    return (
      <div
        key={`row-${field.key}`}
        className={`md:col-span-2 grid grid-cols-1 ${cols} gap-4 rounded-md border border-gray-100 p-3`}
        data-testid={`metadata-row-${field.key}`}
      >
        {renderField(field)}
        {companions.map(renderField)}
      </div>
    );
  };

  /** A companion only counts toward "X / Y fields set" once its parent field is set. */
  const isParentSet = (parentKey: string): boolean => {
    const v = getEffectiveValue(parentKey);
    return !(
      v === undefined ||
      v === null ||
      v === false ||
      v === "" ||
      v === "None" ||
      v === "Null"
    );
  };

  const renderFieldInput = (
    field: MetadataFieldDef,
    value: unknown,
    effectiveValue: unknown,
    inherited: boolean,
  ) => {
    const baseInputClass = `w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
      inherited ? "border-blue-200 bg-blue-50/30" : "border-gray-300"
    } ${readOnly ? "bg-gray-50 cursor-not-allowed" : ""}`;

    switch (field.type) {
      case "dropdown": {
        // Use defaultValue (e.g. "None") as the blank/unset state
        const defaultVal =
          field.defaultValue !== undefined && field.defaultValue !== null
            ? String(field.defaultValue)
            : "";
        const selectedVal =
          value !== undefined ? String(value) : inherited ? "" : defaultVal;
        // Show a blank option when defaultValue is null (e.g. Partner Classification, Paperwork)
        const needsBlankOption = !defaultVal;

        return (
          <select
            value={selectedVal}
            onChange={(e) => {
              // Treat selecting the default as "unset"
              const newVal = e.target.value;
              if (newVal === defaultVal || newVal === "") {
                handleChange(field.key, undefined);
              } else {
                handleChange(field.key, newVal);
              }
            }}
            disabled={readOnly}
            className={baseInputClass}
          >
            {inherited && !value ? (
              <option value="">
                {`${effectiveValue ?? (defaultVal || "—")} (inherited)`}
              </option>
            ) : (
              needsBlankOption && <option value=""></option>
            )}
            {field.options?.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        );
      }

      case "multi_select":
        return (
          <MultiSelectField
            field={field}
            value={value as string[] | undefined}
            effectiveValue={effectiveValue as string[] | undefined}
            inherited={inherited}
            readOnly={readOnly}
            onChange={(val) => handleChange(field.key, val)}
          />
        );

      case "checkbox":
        return (
          <div className="flex items-center space-x-2 pt-1">
            <input
              type="checkbox"
              checked={
                value !== undefined ? Boolean(value) : Boolean(effectiveValue)
              }
              onChange={(e) => handleChange(field.key, e.target.checked)}
              disabled={readOnly}
              className={`h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 ${
                inherited ? "opacity-60" : ""
              }`}
            />
            {inherited && (
              <span className="text-xs text-blue-500">(inherited)</span>
            )}
          </div>
        );

      case "date":
        if (readOnly) {
          const shown =
            value !== undefined
              ? formatDateMDY(String(value))
              : inherited && effectiveValue
                ? `${formatDateMDY(String(effectiveValue))} (inherited)`
                : "";
          return (
            <div
              className={`${baseInputClass} min-h-[38px]`}
              data-testid={`metadata-date-${field.key}`}
            >
              {shown || <span className="text-gray-400">mm/dd/yyyy</span>}
            </div>
          );
        }
        return (
          <input
            type="date"
            value={value !== undefined ? String(value) : ""}
            placeholder={
              inherited && effectiveValue ? String(effectiveValue) : undefined
            }
            onChange={(e) =>
              handleChange(field.key, e.target.value || undefined)
            }
            disabled={readOnly}
            className={baseInputClass}
          />
        );

      case "currency":
        return (
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
              $
            </span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={value !== undefined ? Number(value) : ""}
              placeholder={
                inherited && effectiveValue !== undefined
                  ? String(effectiveValue)
                  : "0.00"
              }
              onChange={(e) =>
                handleChange(
                  field.key,
                  e.target.value ? parseFloat(e.target.value) : undefined,
                )
              }
              disabled={readOnly}
              className={`${baseInputClass} pl-7`}
            />
          </div>
        );

      case "integer":
        return (
          <input
            type="number"
            step="1"
            min="0"
            value={value !== undefined ? Number(value) : ""}
            placeholder={
              inherited && effectiveValue !== undefined
                ? String(effectiveValue)
                : "0"
            }
            onChange={(e) =>
              handleChange(
                field.key,
                e.target.value ? parseInt(e.target.value, 10) : undefined,
              )
            }
            disabled={readOnly}
            className={baseInputClass}
          />
        );

      case "text":
        return (
          <textarea
            value={value !== undefined ? String(value) : ""}
            placeholder={
              inherited && effectiveValue ? String(effectiveValue) : ""
            }
            onChange={(e) =>
              handleChange(field.key, e.target.value || undefined)
            }
            disabled={readOnly}
            rows={2}
            maxLength={255}
            className={baseInputClass}
          />
        );

      case "hyperlink": {
        const shownUrl =
          value !== undefined
            ? String(value)
            : inherited && effectiveValue
              ? String(effectiveValue)
              : "";
        if (readOnly && /^https?:\/\/\S+$/i.test(shownUrl)) {
          // Read-only: a real link that opens in a new tab (http/https only -- never javascript:/data:)
          return (
            <div className={`${baseInputClass} min-h-[38px] truncate`}>
              <a
                href={shownUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800"
                data-testid={`metadata-link-${field.key}`}
              >
                {shownUrl}
              </a>
            </div>
          );
        }
        return (
          <input
            type="url"
            value={value !== undefined ? String(value) : ""}
            placeholder={
              inherited && effectiveValue
                ? String(effectiveValue)
                : "https://..."
            }
            onChange={(e) =>
              handleChange(field.key, e.target.value || undefined)
            }
            disabled={readOnly}
            className={baseInputClass}
          />
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="space-y-4">
      {visibleCategories.map((cat) => {
        const primaries = getPrimaryFieldsByCategory(cat.id);
        const applicable = primaries.flatMap((f) => [
          f,
          ...(isParentSet(f.key) ? getCompanionFields(f.key) : []),
        ]);
        const isExpanded = expandedCategories.has(cat.id);
        const filledCount = applicable.filter(
          (f) => getEffectiveValue(f.key) !== undefined,
        ).length;

        return (
          <div
            key={cat.id}
            className="border border-gray-200 rounded-lg overflow-hidden"
          >
            <button
              type="button"
              onClick={() => toggleCategory(cat.id)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center space-x-2">
                {isExpanded ? (
                  <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                )}
                <span className="text-sm font-semibold text-gray-900">
                  {cat.label}
                </span>
              </div>
              <span className="text-xs text-gray-500">
                {filledCount} / {applicable.length} fields set
              </span>
            </button>

            {isExpanded && (
              <div className="px-4 py-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                {primaries.map(renderRow)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/** Multi-select field rendered as checkbox group */
const MultiSelectField: React.FC<{
  field: MetadataFieldDef;
  value: string[] | undefined;
  effectiveValue: string[] | undefined;
  inherited: boolean;
  readOnly: boolean;
  onChange: (val: string[] | undefined) => void;
}> = ({ field, value, effectiveValue, inherited, readOnly, onChange }) => {
  const selected = value ?? (inherited ? effectiveValue : undefined) ?? [];

  const toggle = (opt: string) => {
    const current = Array.isArray(value) ? value : [];
    const next = current.includes(opt)
      ? current.filter((v) => v !== opt)
      : [...current, opt];
    onChange(next.length > 0 ? next : undefined);
  };

  return (
    <div
      className={`flex flex-wrap gap-2 p-2 border rounded-lg ${
        inherited ? "border-blue-200 bg-blue-50/30" : "border-gray-200"
      }`}
    >
      {field.options?.map((opt) => (
        <label
          key={opt}
          className={`inline-flex items-center space-x-1.5 text-sm cursor-pointer ${
            readOnly ? "cursor-not-allowed opacity-60" : ""
          }`}
        >
          <input
            type="checkbox"
            checked={Array.isArray(selected) && selected.includes(opt)}
            onChange={() => toggle(opt)}
            disabled={readOnly}
            className="h-3.5 w-3.5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          />
          <span>{opt}</span>
        </label>
      ))}
    </div>
  );
};

export default MetadataForm;
